"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import { Section, Typography } from "@/components/ui";
import { pricingSections } from "@/lib/data/pricing-features";
import { env } from "@/lib/environment";
import { useIsAdLanding } from "@/lib/hooks/useIsAdLanding";

const imgBaseUrl = `${env.NEXT_PUBLIC_CDN_URL}/images/pricing`;


function FeatureCell({ value }: { value: boolean | string }) {
  if (typeof value === "string") {
    return <span className="font-medium">{value}</span>;
  }

  return value ? (
    <Image
      src={`${imgBaseUrl}/tick-pricing-black.nwa_Dv3IWXNo.svg`}
      width={19}
      height={19}
      alt="Included"
      className="inline-block"
    />
  ) : (
    <Image
      src={`${imgBaseUrl}/x-mark.-m0P7kqr_fqQ.svg`}
      width={19}
      height={19}
      alt="Not included"
      className="inline-block"
    />
  );
}

export function PricingComparison() {
  const isAdLanding = useIsAdLanding();
  const trialHref = isAdLanding ? "/book-a-demo" : "https://console.pickcel.com/auth/register";
  const trialLabel = isAdLanding ? "Book a Demo" : "Start Free Trial";

  return (
    <Section variant="default" id="pricing-comparison" className="overflow-visible">
    <div className="w-full overflow-visible">
      <div className="hidden md:grid md:grid-cols-12 gap-4 sticky top-[90px] py-8 bg-gray-100 rounded-t-2xl z-[5]">
        <div className="col-span-6 p-4 flex items-center">
          <Typography className="text-2xl font-medium text-text-primary">Compare plans</Typography>
        </div>

        <div className="col-span-2 flex flex-col justify-center items-center">
          <Typography className="text-lg text-text-primary font-medium mb-2">Professional</Typography>
          <Link
            href={trialHref}
            target={isAdLanding ? "_self" : "_blank"}
            rel={isAdLanding ? undefined : "noopener noreferrer"}
            className="inline-block bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors text-sm mb-1 whitespace-nowrap"
          >
            {trialLabel}
          </Link>
          {!isAdLanding && <p className="text-xs text-gray-600">No credit card required.</p>}
        </div>

        <div className="col-span-2 flex flex-col justify-center items-center">
          <Typography className="text-lg text-text-primary font-medium mb-2">Business</Typography>
          <Link
            href={trialHref}
            target={isAdLanding ? "_self" : "_blank"}
            rel={isAdLanding ? undefined : "noopener noreferrer"}
            className="inline-block border-1 border-primary text-primary px-4 py-2 rounded-lg hover:bg-primary hover:text-white transition-colors text-sm mb-1 whitespace-nowrap"
          >
            {trialLabel}
          </Link>
          {!isAdLanding && <p className="text-xs text-gray-600">No credit card required.</p>}
        </div>

        <div className="col-span-2 flex flex-col justify-center items-center">
          <Typography className="text-lg text-text-primary font-medium mb-2">Enterprise</Typography>
          <Link
            href="/company/contact"
            className="inline-block border-1 border-primary text-primary px-4 py-2 rounded-lg hover:bg-primary hover:text-white transition-colors text-sm mb-1 whitespace-nowrap"
          >
            Talk To Us
          </Link>
          <p className="text-xs text-transparent select-none">No credit card required.</p>
        </div>
      </div>

       {/* Pricing Table */}
       <div className="overflow-x-auto" style={{ overflowY: 'visible' }}>
         <table className="w-full border-collapse relative">
           <colgroup>
             <col className="w-1/2" />
             <col className="w-1/6" />
             <col className="w-1/6" />
             <col className="w-1/6" />
           </colgroup>

           {/* Mobile Header */}
           <thead className="md:hidden bg-red-600">
             <tr>
               <th className="p-4" aria-label="Features"></th>
               <th className="p-4 text-white text-center text-[12px]">Professional</th>
               <th className="p-4 text-white text-center text-[12px]">Business</th>
               <th className="p-4 text-white text-center text-[12px]">Enterprise</th>
             </tr>
           </thead>

           <tbody>
             {pricingSections.map((section, sectionIndex) => (
               <React.Fragment key={section.title}>
                 {/* Section Header */}
                 <tr className="bg-gray-100">
                   <th colSpan={1} className="p-4 text-left font-bold text-base">
                     {section.title}
                   </th>
                   <th className="p-4 hidden md:table-cell" aria-label="Professional"></th>
                   <th className="p-4 hidden md:table-cell" aria-label="Business"></th>
                   <th className="p-4 hidden md:table-cell" aria-label="Enterprise"></th>
                 </tr>

                 {/* Features */}
                 {section.features.map((feature, featureIndex) => (
                  <tr
                    key={`${sectionIndex}-${featureIndex}`}
                    className="border-b border-gray-200 hover:bg-gray-50"
                  >
                   <td className="p-4 text-base">
                     <div className="relative inline-flex items-center">
                       {feature.name}
                       {feature.tooltip && (
                         <span className="relative inline-block ml-1 group">
                           <Image
                             src={`${imgBaseUrl}/question.lPsD2W-vdtXb.svg`}
                             width={16}
                             height={16}
                             alt="info"
                             className="inline-block cursor-help"
                           />
                           <span className="invisible group-hover:visible absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg min-w-[240px] max-w-[280px] z-[9999] shadow-2xl whitespace-normal break-words">
                             {feature.tooltip}
                             <span className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-900"></span>
                           </span>
                         </span>
                       )}
                     </div>
                   </td>
                     <td className="p-4 text-center bg-[#fcfafa] border-gray-200">
                       <FeatureCell value={feature.professional} />
                     </td>
                     <td className="p-4 text-center border-gray-200">
                       <FeatureCell value={feature.business} />
                     </td>
                     <td className="p-4 text-center border-gray-200">
                       <FeatureCell value={feature.enterprise} />
                     </td>
                   </tr>
                 ))}
               </React.Fragment>
             ))}
           </tbody>
         </table>
       </div>
    </div>
  </Section>
  );
}

